import Vue from 'vue/dist/vue.esm.js'
import { QUESTION_DEFINITIONS } from './data'
import {
    buildDiagnosisResult,
    buildDiagnosisResultFromTypeKey,
    resultCodeToTypeKey,
    typeKeyToResultCode
} from './diagnosis'

const COMPLETED_DIAGNOSIS_STORAGE_KEY = 'ringSelectionDiagnosisCompleted';

const getResultTypeKeyFromUrl = () => {
    try {
        const params = new URLSearchParams(window.location.search);
        return resultCodeToTypeKey(params.get('r')) || params.get('result');
    } catch (error) {
        return null;
    }
};

const getStorageItem = (key) => {
    try {
        return window.localStorage.getItem(key);
    } catch (error) {
        return null;
    }
};

const initialTranslations = window.ringSelectionDiagnosisTranslations || {};
const initialRingDetails = window.ringSelectionDiagnosisRingData || {};
const initialResult = buildDiagnosisResultFromTypeKey(
    getResultTypeKeyFromUrl(),
    initialRingDetails,
    initialTranslations
);

new Vue({
    el: '#app',
    data: {
        view: initialResult ? 'result' : 'landing',
        currentStep: 0,
        translations: initialTranslations,
        answers: {
            storyEssence: [],
            individualUniversal: [],
            exploreSelect: [],
            dialogueIntrospection: []
        },
        answerHistory: [],
        ringDetails: initialRingDetails,
        baseShareUrl: window.ringSelectionDiagnosisShareUrl || `${window.location.origin}${window.location.pathname}`,
        typeImageBaseUrl: window.ringSelectionDiagnosisTypeImageBaseUrl || '',
        results: initialResult,
        hasCompletedDiagnosis: getStorageItem(COMPLETED_DIAGNOSIS_STORAGE_KEY) === '1',
        activeAxis: null
    },
    computed: {
        questions() {
            const questionTranslations = this.translations.questions || {};

            return QUESTION_DEFINITIONS.map((question) => {
                const translation = questionTranslations[question.id] || {};

                return Object.assign({}, question, {
                    q: translation.q,
                    a: translation.a,
                    b: translation.b
                });
            });
        },
        currentQuestion() {
            return this.questions[this.currentStep];
        },
        progress() {
            return ((this.currentStep + 1) / this.questions.length) * 100;
        },
        showRestart() {
            return this.view === 'result' && !!this.results;
        },
        showBack() {
            return this.answerHistory.length > 0;
        },
        shareUrl() {
            if (!this.results || !this.results.typeKey) return this.baseShareUrl;

            return this.buildResultUrl(this.results.typeKey);
        },
        typeImageUrl() {
            if (!this.results || !this.results.imageSlug || !this.typeImageBaseUrl) return '';

            return `${this.typeImageBaseUrl}/${this.results.imageSlug}.png`;
        },
        resultRestartTitle() {
            const restart = this.translations.restart || {};
            const key = this.hasCompletedDiagnosis ? 'retryTitle' : 'sharedTitle';
            const fallbackKey = this.hasCompletedDiagnosis ? 'retry_title' : 'shared_title';

            return restart[key] || restart[fallbackKey] || '';
        },
        resultRestartText() {
            const restart = this.translations.restart || {};
            const key = this.hasCompletedDiagnosis ? 'retryText' : 'sharedText';
            const fallbackKey = this.hasCompletedDiagnosis ? 'retry_text' : 'shared_text';

            return restart[key] || restart[fallbackKey] || '';
        },
        restartButtonLabel() {
            const restart = this.translations.restart || {};
            if (!this.results) return restart.defaultButton || restart.default_button || '';

            const key = this.hasCompletedDiagnosis ? 'retryButton' : 'sharedButton';
            const fallbackKey = this.hasCompletedDiagnosis ? 'retry_button' : 'shared_button';

            return restart[key] || restart[fallbackKey] || '';
        },
        shareText() {
            const share = this.translations.share || {};
            if (!this.results) return share.defaultText || share.default_text || '';

            const resultText = share.resultText || share.result_text || '';
            return resultText.replace('__TYPE__', this.results.prefType);
        },
        shareContent() {
            return {
                url: this.shareUrl,
                text: this.shareText
            };
        },
        canUseWebShare() {
            return navigator.share && (!navigator.canShare || navigator.canShare(this.shareContent));
        },
        xShareUrl() {
            return `https://twitter.com/intent/tweet?url=${encodeURIComponent(this.shareUrl)}&text=${encodeURIComponent(this.shareText)}`;
        },
        lineShareText() {
            return [this.shareText, this.shareUrl].filter(Boolean).join('\n');
        },
        canUseLineUrlScheme() {
            return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || '');
        },
        lineShareUrl() {
            if (this.canUseLineUrlScheme) {
                return `https://line.me/R/share?text=${encodeURIComponent(this.lineShareText)}`;
            }

            return `https://social-plugins.line.me/lineit/share?url=${encodeURIComponent(this.shareUrl)}&text=${encodeURIComponent(this.shareText)}`;
        }
    },
    methods: {
        startQuiz() {
            this.replaceLocationWithBaseUrl();
            this.view = 'quiz';
        },
        handleAnswer(choice) {
            const q = this.currentQuestion;
            const val = choice === 'a' ? q.valA : q.valB;
            this.answers[q.category].push(val);
            this.answerHistory.push({ step: this.currentStep, category: q.category });
            if (this.currentStep < this.questions.length - 1) {
                this.currentStep++;
            } else {
                this.finishDiagnosis();
            }
        },
        finishDiagnosis() {
            this.results = buildDiagnosisResult(this.answers, this.ringDetails, this.translations);
            this.markDiagnosisCompleted();
            this.view = 'result';
            this.replaceLocationWithResult(this.results.typeKey);
            this.scheduleOfficialShareButtons();
            this.pushResultAnalyticsEvent();
            setTimeout(() => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }, 600);
        },
        resetDiagnosis() {
            const shouldStartQuiz = !!this.results;
            this.answers = {
                storyEssence: [],
                individualUniversal: [],
                exploreSelect: [],
                dialogueIntrospection: []
            };
            this.answerHistory = [];
            this.currentStep = 0;
            this.results = null;
            this.activeAxis = null;
            this.replaceLocationWithBaseUrl();
            this.view = shouldStartQuiz ? 'quiz' : 'landing';
        },
        goBack() {
            const previousAnswer = this.answerHistory.pop();
            if (!previousAnswer) return;

            this.answers[previousAnswer.category].pop();
            this.results = null;
            this.activeAxis = null;
            this.currentStep = previousAnswer.step;
            this.view = 'quiz';
            this.replaceLocationWithBaseUrl();
        },
        shareDiagnosis() {
            if (!this.canUseWebShare) return;

            navigator.share(this.shareContent).catch(() => {});
        },
        openAxisModal(axis) {
            this.activeAxis = axis;
        },
        closeAxisModal() {
            this.activeAxis = null;
        },
        showResultFromUrl() {
            const typeKey = this.getResultTypeKeyFromUrl();
            if (!typeKey) return;

            const result = buildDiagnosisResultFromTypeKey(typeKey, this.ringDetails, this.translations);
            if (!result) return;

            this.results = result;
            this.view = 'result';
            this.replaceLocationWithResult(result.typeKey);
            this.scheduleOfficialShareButtons();
        },
        getResultTypeKeyFromUrl() {
            return getResultTypeKeyFromUrl();
        },
        buildResultUrl(typeKey) {
            const resultCode = typeKeyToResultCode(typeKey);
            if (!resultCode) return this.baseShareUrl;

            try {
                const url = new URL(this.baseShareUrl, window.location.origin);
                url.searchParams.set('r', resultCode);
                return url.toString();
            } catch (error) {
                const separator = this.baseShareUrl.indexOf('?') === -1 ? '?' : '&';
                return `${this.baseShareUrl}${separator}r=${encodeURIComponent(resultCode)}`;
            }
        },
        replaceLocationWithResult(typeKey) {
            if (!window.history || !window.history.replaceState) return;

            window.history.replaceState({}, document.title, this.buildResultUrl(typeKey));
        },
        replaceLocationWithBaseUrl() {
            if (!window.history || !window.history.replaceState) return;

            window.history.replaceState({}, document.title, this.baseShareUrl);
        },
        markDiagnosisCompleted() {
            this.hasCompletedDiagnosis = true;
            this.setStorageItem(COMPLETED_DIAGNOSIS_STORAGE_KEY, '1');
        },
        getStorageItem(key) {
            return getStorageItem(key);
        },
        setStorageItem(key, value) {
            try {
                window.localStorage.setItem(key, value);
            } catch (error) {
                return null;
            }
        },
        loadOfficialShareButtons() {
            if (window.twttr && window.twttr.widgets && window.twttr.widgets.load) {
                window.twttr.widgets.load();
            }
        },
        scheduleOfficialShareButtons() {
            this.$nextTick(() => {
                this.loadOfficialShareButtons();
                setTimeout(this.loadOfficialShareButtons, 300);
                setTimeout(this.loadOfficialShareButtons, 1000);
            });
        },
        pushResultAnalyticsEvent() {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                event: 'ring_diagnosis_result',
                result_type: this.results.typeKey
            });
        }
    },
    mounted() {
        if (this.results) this.replaceLocationWithResult(this.results.typeKey);
        if (this.results) this.scheduleOfficialShareButtons();
    }
});
